// Stevneøkonomi: combine the extracted payment-provider reports stored on
// convention.vippsReports (see backend/lib/convention-report-extract.js) into
// one picture for the economy drawer. Pure functions — no store access.

const round2 = n => Math.round(n * 100) / 100;
const fin = n => (typeof n === 'number' && Number.isFinite(n) ? n : null);
const sumOf = values => round2(values.reduce((acc, v) => acc + (fin(v) || 0), 0));

export const NO_GROUP_LABEL = 'Uten varegruppe';

// Group a sales report's product lines by varegruppe, keeping the report's own
// line order inside each group, groups ordered by amount desc. Returns
// [{ group, lines, qty, amountIncVat, amountExVat, vat }].
export function groupReportLines(lines) {
    const byGroup = new Map();
    (Array.isArray(lines) ? lines : []).forEach(line => {
        if (!line || !line.name) return;
        const group = line.group || NO_GROUP_LABEL;
        if (!byGroup.has(group)) byGroup.set(group, []);
        byGroup.get(group).push(line);
    });
    return [...byGroup.entries()]
        .map(([group, groupLines]) => ({
            group,
            lines: groupLines,
            qty: sumOf(groupLines.map(l => l.qty)),
            amountIncVat: sumOf(groupLines.map(l => l.amountIncVat)),
            amountExVat: sumOf(groupLines.map(l => l.amountExVat)),
            vat: sumOf(groupLines.map(l => l.vat)),
        }))
        .sort((a, b) => b.amountIncVat - a.amountIncVat || a.group.localeCompare(b.group, 'nb'));
}

// Gross figure of one report: sales reports report incl-VAT product totals,
// settlements report captured gross. Null when the report has neither.
export function reportGross(report) {
    if (!report) return null;
    const totals = report.totals || {};
    if (report.kind === 'sales') {
        return fin(totals.incVat) !== null ? totals.incVat : fin(totals.gross);
    }
    return fin(totals.gross) !== null ? totals.gross : fin(totals.incVat);
}

// The combined picture. A sales report and a settlement usually describe the
// SAME money (per-product vs per-transaction), so gross is taken from the
// sales reports when any exist and from the settlements only when none do;
// fees come from settlements alone. `missing` names the kind that is absent so
// the drawer can say what the sum still lacks.
export function summarizeEconomyReports(reports) {
    // Reports switched off after an overlap (includeInSum false) never count.
    const list = (Array.isArray(reports) ? reports : []).filter(r => r && r.md5 && r.includeInSum !== false);
    const sales = list.filter(r => r.kind === 'sales');
    const settlements = list.filter(r => r.kind === 'settlement');
    const others = list.filter(r => r.kind !== 'sales' && r.kind !== 'settlement');

    const salesGross = sumOf(sales.map(reportGross));
    const settlementGross = sumOf(settlements.map(reportGross));
    const grossSource = sales.length > 0 ? 'sales' : (settlements.length > 0 ? 'settlement' : null);
    const gross = grossSource === 'sales' ? salesGross : settlementGross;
    const fees = sumOf(settlements.map(r => r.totals && r.totals.fees));
    const payouts = sumOf(settlements.flatMap(r => (r.salesUnits || []).map(u => u.payouts)));
    const net = round2(gross - fees);

    const missing = [];
    if (sales.length === 0) missing.push('sales');
    if (settlements.length === 0) missing.push('settlement');

    const warningsCount = list.reduce((acc, r) => acc + ((r.warnings || []).length), 0);

    return {
        reportCount: list.length,
        salesCount: sales.length,
        settlementCount: settlements.length,
        otherCount: others.length,
        grossSource,
        gross,
        salesGross,
        settlementGross,
        fees,
        net,
        payouts,
        missing,
        warningsCount,
    };
}

// Strip the data-URL prefix a FileReader.readAsDataURL() result carries.
export function dataUrlToBase64(dataUrl) {
    const str = String(dataUrl || '');
    const idx = str.indexOf('base64,');
    return idx >= 0 ? str.slice(idx + 'base64,'.length) : str;
}
